(ns arthur.domain.gesture
  "Moving, turning and scaling a node by hand on the stage, as channel values.

  A drag says where the pointer went in stage pixels; this says what that makes
  the node's `[:xform :pos]`, `[:xform :rot]`, `[:xform :scale]` or
  `[:xform :pivot]`, given its `nest/placement`. Whatever is above the node —
  instances, parents, a `:pinv` — is in the placement's matrices, so a shape five
  symbols down moves under the pointer like one on top.

  A GESTURE TURNS AND SCALES ABOUT THE NODE'S OWN PIVOT, and nothing here solves
  for a position to fake one with. `node/local!` composes the rotation and the
  scale about `[:xform :pivot]`, so a turn is `rot` alone, ALWAYS — one channel,
  one key, and the pivot held exactly on every frame between two keys because the
  matrix is built about it on every frame. This is Toon Boom's layer pivot and
  Flash's transformation point, and the reason both store one.

  A PIVOT NOBODY HAS CHOSEN IS THE MIDDLE OF WHAT THE NODE DRAWS, and the first
  turn or scale WRITES IT DOWN — `pivot` derives it from `pick/bounds-of` on the
  frame the drag starts, and `with-pivot` turns that into a `[:xform :pivot]` and
  the `[:xform :pos]` that leaves the picture exactly where it is. After that it
  is an ordinary stored, keyable, draggable channel, and the derived value is
  never consulted again: a pivot is a CHOICE, and re-deriving it per drag means
  editing a symbol silently moves what its instances turn about.

  WHAT THIS REPLACES, because it was a deletion that cost a feature. There used
  to be no pivot in the decomposition at all: a drag derived the middle of the
  drawing, and `about` solved for the `pos` that holds that point still under the
  new angle. That solution is an ARC in the angle while `pos` interpolates along
  the CHORD, so it is exact on the frame it is written and WRONG EVERYWHERE
  BETWEEN TWO KEYS. A drawing escaped it, because `paint/centred` puts a shape's
  origin on the middle of what it draws and the correction is then nil — but a
  SYMBOL INSTANCE cannot: its origin is its symbol's, and a symbol is drawn on the
  stage, so its origin is the stage's top-left corner. One keyed turn of an
  instance therefore swung its drawing round the corner of the stage on an orbit
  the size of the stage. The answer at the time was a peg, and a peg is a real
  thing — it is how a pivot is SHARED, or put over a measured transform — but it
  is not something anybody should have to make in order to spin a drawing.

  `about` is still here and is still that equation, for the one gesture that
  genuinely has a pivot belonging to no node: a MULTI-SELECTION scaling about the
  middle of its shared box. Nobody keys that.

  The normal keying rule is `node/set-channel`, the inspector's: a channel with
  keys gets one on the node's own frame, and one without has its one value
  changed. Auto-key deliberately replaces that rule with `set-keyed-channel`,
  so touching an otherwise static transform starts its animation at this frame."
  (:require [arthur.domain.channel :as ch]
            [arthur.domain.node :as node]))

(defn values
  "Node `n`'s transform on its own frame `f`, as vectors.

  `store` IS NOT OPTIONAL, though `ch/value-at` would let it be. A measured
  transform is a dense channel, and a dense channel read without the tier-2
  store it names throws — so leaving it off read correctly for every hand-placed
  node and crashed the stage the moment a selection landed on an iris, a brow or
  a head. Those are not hard to land on: `pick/choose` keeps a selection at the
  depth it already has, so once anything inside a face is selected, an ordinary
  click beside it selects its neighbour — which near the eyes is an iris."
  [n f store]
  (let [at #(ch/value-at (get (node/channels n) [:xform %]) f store)
        xy #(let [v (at %)] [(ch/component v 0) (ch/component v 1)])]
    {:pos (xy :pos) :pivot (xy :pivot) :rot (at :rot)
     :scale (xy :scale) :skew (xy :skew)
     ;; WHETHER THE NODE HAS A PIVOT OF ITS OWN, and not what it is: a node
     ;; with no `[:xform :pivot]` channel reads `[0 0]` off `node/defaults`,
     ;; which is a real pivot — a drawing's own middle — and also what a node
     ;; nobody has pivoted yet looks like. The two have to be told apart
     ;; exactly once, when a drag decides whether to write the derived middle
     ;; down; see `pivot` and `with-pivot`.
     :chosen? (contains? (:channels n) [:xform :pivot])}))

(defn- measured-channel? [n path]
  (let [c (get-in n [:channels path])]
    (boolean (or (:dense c) (:generated c)))))

(defn refusal
  "Why `kind` cannot transform node `n`, or nil. Measured position and rotation
  accept authored correction layers; measured scale cannot yet be decomposed
  safely. The one-argument form asks whether any stage gesture is possible."
  ([n] (when (node/measured? n)
         "its transform is measured — use a correction, or put a peg over it"))
  ([n kind]
   (when (and (= :scale kind) (measured-channel? n [:xform :scale]))
     "its scale is measured — put a peg over it and scale that")))

(defn- through [m [x y]]
  (let [out (js/Float64Array. 2)]
    (node/apply-pt! out 0 m x y)
    [(aget out 0) (aget out 1)]))

(defn linear
  "The node's own linear part, `R(rot)·K(skew)·S(scale)`, as a 2x3 whose
  translation is zero — so putting a point through it applies the rotation, skew
  and scale and nothing else. `node/local!` with a zero `pos` rather than a
  second closed form, so there is one place the decomposition is written out."
  [{:keys [rot scale skew]}]
  (node/local! (node/mat) [0 0] [0 0] rot scale skew))

(defn local-of
  "The node's whole local transform, `T(pos)·T(piv)·R·K·S·T(-piv)` — what takes a
  point in the node's own coordinates to its parent's."
  [{:keys [pos pivot rot scale skew]}]
  (node/local! (node/mat) pos pivot rot scale skew))

(defn about
  "The `pos` that keeps parent-space point `c` still while the node's linear part
  changes from `v`'s to `v'`'s. Nil when `v`'s is singular — a node scaled to
  nothing has no point under `c` to hold.

  FOR A PIVOT THAT IS NO NODE'S, which since the pivot went into the
  decomposition is one gesture and only one: a multi-selection scaling about the
  middle of its shared box, where every member has to move to keep the
  arrangement and none of them owns the point. `turn` and `scale` do not call
  this, and the namespace docstring says why — the position it solves for is an
  ARC in the angle and `pos` tweens along the CHORD, so it is exact on the frame
  it is written and wrong between two keys.

  Local is `T(t)·M` with `t = pos + a − M·a`, the composed translation. The
  material point sitting under `c` is `q = M⁻¹(c − t)`, holding it there under the
  new `M'` wants `t' = c − M'·q`, and the `pos` that composes to that `t'` is

      p' = t' − a + M'·a  =  c − M'·(q − a) − a

  Checkable at both ends it has to be right at: with `c` the node's own pivot
  point, `c = pos + a`, so `q = a` and `p' = c − a = pos` — turning about your own
  pivot never moves you. And with no pivot at all, `a = 0`, it is the familiar
  `p' = c − M'·M⁻¹(c − p)`."
  [v v' c]
  (let [m (linear v)
        l (local-of v)
        t [(aget l 4) (aget l 5)]
        a (:pivot v)]
    (when-let [inv (node/invert m)]
      (let [q (through inv (mapv - c t))]
        (mapv - (mapv - c (through (linear v') (mapv - q a))) a)))))

(defn middle
  "The middle of `bounds` — what the node draws, in its own coordinates — through
  its own transform, so a parent-space point; or its own origin when it draws
  nothing. What a node nobody has pivoted yet turns about."
  [v bounds]
  (let [[x0 y0 x1 y1] bounds]
    (through (local-of v)
             (if bounds [(/ (+ x0 x1) 2) (/ (+ y0 y1) 2)] [0 0]))))

(defn pivot
  "The parent-space point a drag on this node turns and scales about: ITS OWN
  PIVOT, `pos + piv` — or, for a node nobody has pivoted yet, the middle of
  `bounds`, what it draws in its own coordinates, through its own transform.

  THE CHOSEN ONE WINS, and that is the whole of the rule. A pivot is where
  somebody put it: it does not follow the drawing afterwards, any more than
  Flash's transformation point or a Harmony layer's pivot does, because a turn
  that quietly changes its centre when a symbol is edited is worse than one
  sitting somewhere a hand can see and move it. `ui/stage` draws the cross here
  and `::ui/repivot` drags it.

  THE DERIVED ONE IS A DEFAULT AND NOT A BEHAVIOUR. `pick/bounds-of` on the same
  frame is where the bounds come from, so the cross and the selection box start
  out as one computation — and the first turn or scale WRITES IT DOWN, which is
  `with-pivot`. A `:group` draws nothing, so `bounds` is nil and this is its own
  origin, which is where a peg was put.

  `middle` is the derived half on its own, for `centred` — which is the way back
  to it once a pivot HAS been chosen."
  [v bounds]
  (if (:chosen? v) (mapv + (:pos v) (:pivot v)) (middle v bounds)))

(defn at-pivot?
  "Is parent-space point `c` where the node already pivots?

  WITHIN A MILLIONTH OF A PIXEL, because this asks a question about intent and
  gets an answer in floating point: `paint/centred` subtracts the middle of a
  ring from its own points, so re-deriving that middle from the result lands on
  zero to within the rounding of the subtraction, a part in 1e14 of the
  coordinates. A point that close to the pivot IS the pivot — there is no gesture
  in which a millionth of a pixel is a pivot somewhere else — and the whole point
  of asking is to leave a drawing's channels alone: a pivot of `[0 0]` written
  onto a shape that already turns about its own middle is a key nobody asked for
  on a value that was already right."
  [v c]
  (let [[dx dy] (mapv - c (mapv + (:pos v) (:pivot v)))]
    (< (js/Math.hypot dx dy) 1e-6)))

(defn repivot
  "The channel values that put the node's pivot on parent-space point `c` WITHOUT
  MOVING THE PICTURE, or nil when its linear part is singular.

  Local is `T(pos)·T(a)·M·T(-a)`, so the new pivot has to be the material point
  that is under `c` now, and the new position has to put it there:

      a' = a + M⁻¹(c − (pos + a))        the point under c, in the node's own space
      p' = c − a'                        so that p' + a' = c

  and then `p' + a' + M(x − a')` is `pos + a + M(x − a)` for EVERY x, which is
  the \"moving nothing\" in the first line, exact and not to a tolerance.

  WITH NO ROTATION OR SCALE IT DOES NOT TOUCH `pos` AT ALL — `M = I` gives
  `a' = c − pos` and `p' = pos` — which is the ordinary case of setting a pivot up
  before animating, and is why this can be done quietly inside a first turn
  without starting a position channel nobody asked for.

  EXACT ON THE FRAME IT IS WRITTEN. `M` is this frame's, so on a node whose
  rotation or scale is already keyed, the compensation that holds the picture
  still here is not the one that would hold it still three frames later. That is
  not an artefact of the arithmetic: moving a pivot genuinely changes what the
  keyed angles mean. Flash and Harmony both let you do it and both move the
  in-betweens; the alternative is refusing to repivot anything already animated,
  which is the node a pivot is most often wrong on."
  [v c]
  (when-let [inv (node/invert (linear v))]
    (let [a' (mapv + (:pivot v) (through inv (mapv - c (mapv + (:pos v) (:pivot v)))))]
      {[:xform :pivot] a'
       [:xform :pos]   (mapv - c a')})))

(defn centred
  "The channel values that put the node's pivot back on the middle of what it
  draws NOW, moving nothing. Nil when its linear part is singular.

  THE WAY BACK, and the thing a stored pivot needs in order to be safe to store.
  A pivot does not follow the drawing — that is the point of storing it, since a
  keyed spin must not be re-aimed by someone drawing one more shape inside the
  symbol — but a drawing does grow, and \"put it back in the middle of what is
  there now\" is then an obvious thing to want and an unobvious thing to do by
  hand. It is `repivot` at the point `pivot` would have derived, so the button
  and the default cannot disagree: this is exactly where an untouched node's
  cross already is."
  [v bounds]
  (repivot v (middle v bounds)))

(defn- with-pivot
  "`[v vs]`: the node's transform with its pivot on parent-space `c`, and the
  channel values that put it there — or `v` untouched and nil, when that is where
  it pivots already or when it has a pivot of its own.

  THE ONE PLACE A DERIVED PIVOT BECOMES A STORED ONE. `turn` and `scale` both
  start here, so the first drag on a node nobody has pivoted writes the middle of
  what it draws down with the gesture, in the same edit, and every drag after it
  turns about the stored one. The returned `v` carries the new pivot, because the
  gesture itself is measured about it: scaling about the pivot it is in the act of
  choosing is one answer, not two."
  [v c]
  (if (and c (not (:chosen? v)) (not (at-pivot? v c)))
    (if-let [vs (repivot v c)]
      [(assoc v :pivot (get vs [:xform :pivot]) :pos (get vs [:xform :pos])) vs]
      [v nil])
    [v nil]))

(defn move
  "The node's position with the drag carried from stage point `p0` to `p1`.

  ONE CHANNEL, AND IT NEVER DISTURBS THE PIVOT: `[:xform :pivot]` is in the
  node's own coordinates, so it travels with the node and a move is `pos` alone,
  exactly as it was before there was a pivot at all."
  [{:keys [parent]} {:keys [pos]} p0 p1]
  (when-let [inv (node/invert parent)]
    {[:xform :pos] (mapv + pos (mapv - (through inv p1) (through inv p0)))}))

(defn angle
  "The angle of stage point `p` about parent-space pivot `c`, in the space the
  node's rotation is in."
  [{:keys [parent]} c p]
  (when-let [inv (node/invert parent)]
    (let [[x y] (mapv - (through inv p) c)]
      (js/Math.atan2 y x))))

(defn turn
  "The node turned by `da` radians about parent-space point `c`: the rotation, and
  — only on the first turn of a node nobody has pivoted — the pivot and position
  that put its pivot on `c` without moving it.

  ONE CHANNEL, ONCE THE PIVOT IS ITS OWN, and that is the whole reason the pivot
  is in `node/local!` rather than solved for here. `rot` is the only thing a turn
  changes, so a keyed turn interpolates ONE number and the matrix is composed
  about the pivot on every frame of it: the pivot is held exactly between two keys
  and not merely at them. A `pos` written beside the rotation — which is what
  `about` solves for, and what this used to do for anything whose origin was not
  its middle — tweens along the chord of an arc it has no way to know about, and a
  360° turn keyed that way leaves the stage in the middle and comes back.

  `c` IS A DEFAULT, NOT A TARGET. A node with its own pivot ignores it and turns
  about what it has, which is what makes a pivot something a hand can place and
  rely on; `pivot` is where `c` comes from either way."
  [v c da]
  (let [[v vs] (with-pivot v c)]
    (merge vs {[:xform :rot] (+ (:rot v) da)})))

(defn scale
  "The node's scale with the point under stage `p0` taken to `p1`, about its own
  pivot, along the node's own axes — or by the same factor on both when
  `uniform?` — and, on the first scale of a node nobody has pivoted, the pivot and
  position that put its pivot on `c` without moving it.

  The factors are measured in the node's OWN coordinates, which is what makes a
  corner drag track the pointer on a node that has been turned, and they are
  measured FROM THE PIVOT `q`: scaling by `k` takes `q + d` to `q + k·d`, so the
  factor a corner wants is the ratio of its offsets from the pivot before and
  after. `with-pivot` runs first because a pivot being chosen by this very drag is
  the pivot the drag has to be measured about."
  [{:keys [world]} v c p0 p1 uniform?]
  (let [[v vs] (with-pivot v c)]
    (when-let [winv (node/invert world)]
      (let [q (:pivot v)
            a (mapv - (through winv p0) q)
            b (mapv - (through winv p1) q)
            k (fn [a b] (if (< (js/Math.abs a) 1e-6) 1 (/ b a)))
            r (when uniform?
                (let [aa (reduce + (map * a a))]
                  (if (< aa 1e-9) 1 (/ (reduce + (map * a b)) aa))))
            s  (:scale v)
            s' (if r (mapv #(* r %) s) (mapv * s (map k a b)))]
        (merge vs {[:xform :scale] s'})))))

(defn scale-by
  "The node scaled by factor `k` on both axes about parent-space point `c`, and
  the position that holds `c` still.

  What a MULTI-SELECTION scales by: one factor for everything about the shared
  box, so a group of shapes keeps its arrangement instead of each member solving
  for its own factors. The point belongs to the box and not to any node in it, so
  this is the one gesture that still writes a position to hold a pivot still —
  `about`, with everything its docstring says that costs. `scale` is the
  single-node form, where the factors come out of the pointer in the node's own
  axes and the pivot is the node's own."
  [v c k]
  (let [v' (update v :scale #(mapv (partial * k) %))]
    (cond-> {[:xform :scale] (:scale v')}
      c (into (when-let [p (about v v' c)] {[:xform :pos] p})))))

(def ^:private manual-layer ::manual-transform)

(defn- plus [a b]
  (if (number? a) (+ a b) (mapv + (vec a) (vec b))))

(defn- minus [a b]
  (if (number? a) (- a b) (mapv - (vec a) (vec b))))

(defn- corrected-channel
  "Set the effective value of a measured channel without replacing its base."
  [channel f target store extent auto-key?]
  (let [current (ch/value-at channel f store)
        layers  (vec (:over channel))
        i       (first (keep-indexed #(when (= manual-layer (:id %2)) %1) layers))
        old     (when i (ch/value-at (get-in layers [i :values]) f store))
        zero    (if (number? target) 0 (vec (repeat (count target) 0)))
        value   (plus (or old zero) (minus target current))
        values  (or (when i (get-in layers [i :values])) (ch/framed zero))
        values  ((if auto-key? node/set-keyed-channel node/set-channel)
                 {:kind :group :channels {[:xform :pos] values}}
                 [:xform :pos] f value)
        values  (get-in values [:channels [:xform :pos]])
        layer   (ch/layer manual-layer [0 extent] :offset values)]
    (assoc channel :over (if i (assoc layers i layer) (conj layers layer)))))

(defn apply-values
  "Clip with channel values `vs`, `{path value}`, written into node `id` of
  symbol `sid` on the node's own frame `f`, by the keying rule above. The sixth
  argument arms auto-key; the five-argument form retains the normal rule."
  ([clip sid id f vs] (apply-values clip sid id f vs false nil))
  ([clip sid id f vs auto-key?] (apply-values clip sid id f vs auto-key? nil))
  ([clip sid id f vs auto-key? store]
   (let [put (if auto-key? node/set-keyed-channel node/set-channel)]
     (update-in clip [:symbols sid :nodes id]
                #(reduce-kv
                  (fn [n path v]
                    (if (measured-channel? n path)
                      (update-in n [:channels path] corrected-channel f v store
                                 (get-in clip [:symbols sid :frames]) auto-key?)
                      (put n path f v)))
                  % vs)))))

(defn apply-take
  "Apply a buffered performance take. `take` is keyed by `[symbol node]`, then
  local frame, then channel path. It becomes ordinary authored keys in one
  document edit rather than making the edit pipeline run for every sample."
  ([clip take] (apply-take clip take nil))
  ([clip take store]
   (reduce-kv
    (fn [c [sid id] frames]
      (reduce-kv (fn [c f values]
                   (apply-values c sid id f values true store))
                 c frames))
    clip take)))
